import { useState, useEffect, useRef } from "react";
import { Mic, Square, Keyboard, Loader2, Volume2, Play, Plus, Check, Sparkles, BookOpen, Trash2, Pencil, ArrowLeft, Save, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import Waveform from "@/components/Waveform";
import { Bidi } from "@/lib/bidi";
import { langName, voiceOK, ttsOK } from "@/lib/languages";

export default function VoiceJournal() {
  const { t } = useI18n();
  const { user } = useAuth();
  const targetCode = user?.target_lang || "es";
  const nativeCode = user?.native_lang || "en";
  const targetLabel = langName(targetCode);

  const [tab, setTab] = useState("compose");           // compose | archive
  const [inputMode, setInputMode] = useState(voiceOK(targetCode) ? "speak" : "type");  // speak | type
  const [transcript, setTranscript] = useState("");
  const [hasDraft, setHasDraft] = useState(false);       // transcript ready for review
  const [result, setResult] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [saved, setSaved] = useState({});
  const [practiceB64, setPracticeB64] = useState(null);
  const [recMode, setRecMode] = useState(null);          // input | practice
  const [savedEntry, setSavedEntry] = useState(false);

  const { recording, analyser, start, stop, audioMime, audioMeta } = useAudioRecorder();
  const audioRef = useRef(null);

  // ---- Archive ----
  const [entries, setEntries] = useState([]);
  const [openEntry, setOpenEntry] = useState(null);
  const loadEntries = () => api.get("/journal/entries").then((r) => setEntries(r.data.entries || [])).catch(() => {});
  useEffect(() => { if (tab === "archive") loadEntries(); }, [tab]);

  const resetCompose = () => {
    setTranscript(""); setHasDraft(false); setResult(null);
    setSaved({}); setPracticeB64(null); setSavedEntry(false);
  };

  // ---- Record (input) ----
  const toggleRecord = async (mode) => {
    if (recording) {
      const b64 = await stop();
      if (recMode === "practice") {
        setPracticeB64(b64); setRecMode(null);
        if (b64 && result) api.post("/journal/practice", { practice_id: `jp${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`, items: [...(result.vocabulary || []).map((v) => v.word), ...(result.phrases || []).map((p) => p.phrase)].filter(Boolean) }).catch(() => {});
        return;
      }
      setRecMode(null);
      if (!b64) { toast.error(t("journal.micDenied")); return; }
      setTranscribing(true);
      try {
        const r = await api.post("/journal/transcribe", { audio: b64, mime: audioMime.current, meta: audioMeta.current });
        setTranscript(r.data.transcript); setHasDraft(true);
      } catch (e) {
        const st = e?.response?.status;
        if (st === 402) toast.error("Voice Journal is a premium feature.");
        else toast.error(e?.response?.data?.detail || t("journal.micDenied"));
        if (st === 502) setInputMode("type");
      } finally { setTranscribing(false); }
      return;
    }
    try {
      setRecMode(mode);
      await start();
    } catch {
      setRecMode(null);
      toast.error(t("journal.micDenied"));
      setInputMode("type");
    }
  };

  const seeHowToSay = async () => {
    if (!transcript.trim()) return;
    setAnalyzing(true); setResult(null); setSavedEntry(false); setSaved({});
    try {
      const r = await api.post("/journal/analyze", { transcript: transcript.trim() });
      setResult(r.data);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Voice Journal is a premium feature.");
      else toast.error(e?.response?.data?.detail || "Could not translate — try again.");
    } finally { setAnalyzing(false); }
  };

  const listen = async (text) => {
    if (!text || playing) return;
    setPlaying(true);
    try {
      const r = await api.post("/journal/tts", { text });
      const audio = new Audio(`data:audio/mp3;base64,${r.data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch { setPlaying(false); toast.error("Couldn't play audio."); }
  };

  const playPractice = async () => {
    if (!practiceB64) return;
    const audio = new Audio(`data:audio/webm;base64,${practiceB64}`);
    try { await audio.play(); } catch { toast.error("Couldn't play your recording."); }
  };

  const addVocab = async (items) => {
    const fresh = (items || []).filter((v) => v.word && !saved[v.word]);
    if (!fresh.length) return;
    try {
      await api.post("/vocab/add", {
        items: fresh.map((v) => ({ word: v.word, translation: v.translation, example_sentence: v.note || result?.target_text?.slice(0, 160) || "" })),
        source: "voice_journal",
      });
      setSaved((s) => { const n = { ...s }; fresh.forEach((v) => { n[v.word] = true; }); return n; });
      toast.success(`${t("common.added")}: ${fresh.length}`);
    } catch { toast.error("Couldn't add to Vocab Bank."); }
  };

  const saveEntry = async () => {
    try {
      await api.post("/journal/entries", {
        native_transcript: transcript.trim(),
        target_text: result.target_text,
        vocabulary: result.vocabulary, phrases: result.phrases,
        grammar: result.grammar, actions: result.actions,
        saved_vocab: Object.keys(saved),
      });
      setSavedEntry(true);
      toast.success(t("journal.saved"));
    } catch { toast.error("Couldn't save the entry."); }
  };

  const renameEntry = async (e) => {
    const title = window.prompt(t("journal.rename"), e.title || "");
    if (title === null) return;
    try { await api.patch(`/journal/entries/${e.entry_id}`, { title }); loadEntries(); if (openEntry?.entry_id === e.entry_id) setOpenEntry({ ...openEntry, title }); }
    catch { toast.error("Couldn't rename."); }
  };
  const deleteEntry = async (id) => {
    try { await api.delete(`/journal/entries/${id}`); setOpenEntry(null); loadEntries(); toast.success(t("journal.deleted")); }
    catch { toast.error("Couldn't delete."); }
  };

  const VocabList = ({ items, label }) => (
    (items && items.length) ? (
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-cabinet font-bold text-sm uppercase tracking-wide text-gray-600">{label}</h4>
          <button onClick={() => addVocab(items)} className="nb-btn-outline py-1 px-2 text-xs" data-testid="journal-add-all">
            <Plus className="w-3 h-3" /> {t("common.addAll")}
          </button>
        </div>
        <div className="space-y-1.5">
          {items.map((v, i) => (
            <div key={i} className="flex items-center justify-between gap-2 border-2 border-ink rounded-xl px-3 py-2 bg-white" data-testid="journal-vocab-row">
              <div className="min-w-0">
                <Bidi code={targetCode} className="font-bold" as="div">{v.word}</Bidi>
                <Bidi code={nativeCode} className="text-xs text-gray-500" as="div">{v.translation}{v.note ? ` · ${v.note}` : ""}</Bidi>
              </div>
              <button onClick={() => addVocab([v])} disabled={!!saved[v.word]} className={`shrink-0 py-1 px-2 text-xs rounded-lg border-2 border-ink font-bold ${saved[v.word] ? "bg-mint" : "bg-butter"}`} data-testid="journal-add-word">
                {saved[v.word] ? <><Check className="w-3 h-3 inline" /> {t("common.added")}</> : <><Plus className="w-3 h-3 inline" /> {t("common.add")}</>}
              </button>
            </div>
          ))}
        </div>
      </div>
    ) : null
  );

  return (
    <AppLayout>
      <Seo title="Voice Journal · FluentLoop" />
      <div className="max-w-3xl mx-auto" data-testid="voice-journal">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h1 className="font-cabinet font-extrabold text-3xl flex items-center gap-2"><Sparkles className="w-7 h-7 text-coral" /> {t("journal.title")}</h1>
          <div className="flex items-center gap-1 bg-paper border-2 border-ink rounded-xl p-0.5">
            <button onClick={() => { setTab("compose"); }} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${tab === "compose" ? "bg-ink text-white" : ""}`} data-testid="journal-tab-compose">{t("journal.newEntry")}</button>
            <button onClick={() => { setTab("archive"); setOpenEntry(null); }} className={`px-3 py-1.5 rounded-lg text-sm font-bold ${tab === "archive" ? "bg-ink text-white" : ""}`} data-testid="journal-tab-archive">{t("journal.archive")}</button>
          </div>
        </div>
        <p className="text-gray-600 text-sm mb-5">{t("journal.tagline")}</p>

        {/* ---------------- COMPOSE ---------------- */}
        {tab === "compose" && (
          <div className="space-y-5">
            {/* input */}
            {!hasDraft && !result && (
              <div className="nb-card p-6">
                <p className="text-sm text-gray-600 mb-4">{t("journal.intro")}</p>
                <div className="flex items-center gap-2 mb-4">
                  {voiceOK(targetCode) && <button onClick={() => setInputMode("speak")} className={`flex-1 py-2 rounded-xl border-2 border-ink font-bold text-sm ${inputMode === "speak" ? "bg-mint" : "bg-white"}`} data-testid="journal-mode-speak"><Mic className="w-4 h-4 inline" /> {t("journal.speak")}</button>}
                  <button onClick={() => setInputMode("type")} className={`flex-1 py-2 rounded-xl border-2 border-ink font-bold text-sm ${inputMode === "type" ? "bg-mint" : "bg-white"}`} data-testid="journal-mode-type"><Keyboard className="w-4 h-4 inline" /> {t("journal.type")}</button>
                </div>
                {inputMode === "speak" ? (
                  <div className="text-center py-6">
                    <button onClick={() => toggleRecord("input")} disabled={transcribing} className={`w-20 h-20 rounded-full border-2 border-ink flex items-center justify-center mx-auto transition-all ${recording ? "bg-coral animate-pulse" : "bg-butter"}`} data-testid="journal-record-btn">
                      {transcribing ? <Loader2 className="w-8 h-8 animate-spin" /> : recording ? <Square className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
                    </button>
                    <p className="text-sm text-gray-600 mt-3">{transcribing ? t("journal.transcribing") : recording ? t("journal.recording") : t("journal.speak")}</p>
                    {recording && recMode === "input" && <div className="mt-3 max-w-xs mx-auto" data-testid="journal-level-meter"><Waveform analyser={analyser} active color="#FF6B6B" height={40} bars={28} /></div>}
                  </div>
                ) : (
                  <div>
                    <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} dir="auto" rows={4} placeholder={t("journal.typePlaceholder")} className="nb-input w-full" data-testid="journal-type-input" />
                    <button onClick={() => transcript.trim() && setHasDraft(true)} disabled={!transcript.trim()} className="nb-btn-primary w-full mt-3 py-2.5" data-testid="journal-type-continue">{t("common.continue")}</button>
                  </div>
                )}
              </div>
            )}

            {/* review transcript */}
            {hasDraft && !result && (
              <div className="nb-card p-6" data-testid="journal-review">
                <h3 className="font-cabinet font-bold text-lg">{t("journal.review")}</h3>
                <p className="text-sm text-gray-600 mt-1 mb-3">{t("journal.reviewHelp")}</p>
                <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} dir="auto" rows={4} className="nb-input w-full" data-testid="journal-review-input" />
                <div className="flex items-center gap-2 mt-3">
                  <button onClick={() => { resetCompose(); }} className="nb-btn-outline py-2.5 px-4" data-testid="journal-retry">{t("journal.retry")}</button>
                  <button onClick={seeHowToSay} disabled={analyzing || !transcript.trim()} className="nb-btn-primary flex-1 py-2.5" data-testid="journal-see-how">
                    {analyzing ? <><Loader2 className="w-4 h-4 inline animate-spin" /> {t("journal.analyzing")}</> : <><Sparkles className="w-4 h-4 inline" /> {t("journal.seeHowToSay")}</>}
                  </button>
                </div>
              </div>
            )}

            {/* result */}
            {result && (
              <div className="space-y-4" data-testid="journal-result">
                <div className="nb-card p-5 bg-paper">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500">{t("journal.whatYouSaid")}</h4>
                  <Bidi code={nativeCode} as="p" className="mt-1">{transcript}</Bidi>
                </div>
                <div className="nb-card p-5 bg-sky">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-gray-600">{t("journal.howToSay", { lang: targetLabel })}</h4>
                  <Bidi code={targetCode} as="p" className="mt-1 text-lg font-medium" data-testid="journal-target-text">{result.target_text}</Bidi>
                  <div className="flex items-center gap-2 mt-3">
                    {ttsOK(targetCode) && <button onClick={() => listen(result.target_text)} disabled={playing} className="nb-btn-mint py-2 px-4 text-sm" data-testid="journal-listen"><Volume2 className="w-4 h-4 inline" /> {t("common.listen")}</button>}
                    {voiceOK(targetCode) && !recording && recMode !== "practice" && <button onClick={() => toggleRecord("practice")} className="nb-btn-outline py-2 px-4 text-sm" data-testid="journal-practice"><Mic className="w-4 h-4 inline" /> {t("journal.practice")}</button>}
                    {recording && recMode === "practice" && <button onClick={() => toggleRecord("practice")} className="nb-btn-outline py-2 px-4 text-sm bg-coral" data-testid="journal-practice-stop"><Square className="w-4 h-4 inline" /> {t("common.stop")}</button>}
                    {practiceB64 && <button onClick={playPractice} className="nb-btn-outline py-2 px-4 text-sm" data-testid="journal-play-practice"><Play className="w-4 h-4 inline" /> {t("journal.playYours")}</button>}
                  </div>
                </div>

                <VocabList items={result.vocabulary} label={t("journal.usefulWords")} />
                {result.phrases?.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-cabinet font-bold text-sm uppercase tracking-wide text-gray-600">{t("journal.usefulPhrases")}</h4>
                      <button onClick={() => addVocab(result.phrases.map((p) => ({ word: p.phrase, translation: p.translation })))} className="nb-btn-outline py-1 px-2 text-xs"><Plus className="w-3 h-3" /> {t("common.addAll")}</button>
                    </div>
                    <div className="space-y-1.5">
                      {result.phrases.map((p, i) => (
                        <div key={i} className="border-2 border-ink rounded-xl px-3 py-2 bg-white" data-testid="journal-phrase-row">
                          <Bidi code={targetCode} as="div" className="font-bold">{p.phrase}</Bidi>
                          <Bidi code={nativeCode} as="div" className="text-xs text-gray-500">{p.translation}</Bidi>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {result.grammar?.length > 0 && (
                  <div className="nb-card p-4">
                    <h4 className="font-cabinet font-bold text-sm uppercase tracking-wide text-gray-600 mb-2"><BookOpen className="w-4 h-4 inline" /> {t("journal.grammar")}</h4>
                    {result.grammar.map((g, i) => (
                      <div key={i} className="mb-2"><div className="font-bold text-sm">{g.point}</div><Bidi code={nativeCode} as="div" className="text-sm text-gray-600">{g.explanation}</Bidi></div>
                    ))}
                  </div>
                )}
                {result.actions?.length > 0 && (
                  <div className="nb-card p-4 bg-butter" data-testid="journal-actions">
                    <h4 className="font-cabinet font-bold text-sm uppercase tracking-wide text-gray-700 mb-2"><ListChecks className="w-4 h-4 inline" /> {t("journal.actions")}</h4>
                    <ul className="list-disc list-inside text-sm">
                      {result.actions.map((a, i) => <li key={i} dir="auto">{a}</li>)}
                    </ul>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => resetCompose()} className="nb-btn-outline py-2.5 px-4" data-testid="journal-new">{t("journal.newEntry")}</button>
                  <button onClick={saveEntry} disabled={savedEntry} className="nb-btn-primary flex-1 py-2.5" data-testid="journal-save-entry">
                    {savedEntry ? <><Check className="w-4 h-4 inline" /> {t("journal.saved")}</> : <><Save className="w-4 h-4 inline" /> {t("journal.saveEntry")}</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ---------------- ARCHIVE ---------------- */}
        {tab === "archive" && !openEntry && (
          <div className="space-y-2" data-testid="journal-archive-list">
            {entries.length === 0 && <div className="nb-card p-8 text-center text-gray-500">{t("journal.empty")}</div>}
            {entries.map((e) => (
              <div key={e.entry_id} className="nb-card p-4 flex items-center justify-between gap-2" data-testid={`journal-entry-${e.entry_id}`}>
                <button onClick={() => setOpenEntry(e)} className="min-w-0 text-left flex-1">
                  <div className="font-cabinet font-bold truncate">{e.title}</div>
                  <Bidi code={e.target_language} as="div" className="text-sm text-gray-500 truncate">{e.target_text}</Bidi>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => renameEntry(e)} className="nb-btn-outline py-1.5 px-2 text-xs" data-testid="journal-rename"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => deleteEntry(e.entry_id)} className="nb-btn-outline py-1.5 px-2 text-xs" data-testid="journal-delete"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "archive" && openEntry && (
          <div className="space-y-4" data-testid="journal-entry-detail">
            <button onClick={() => setOpenEntry(null)} className="inline-flex items-center gap-1 text-sm font-bold text-gray-600"><ArrowLeft className="w-4 h-4" /> {t("journal.archive")}</button>
            <h2 className="font-cabinet font-extrabold text-2xl">{openEntry.title}</h2>
            <div className="nb-card p-5 bg-paper"><h4 className="text-xs font-bold uppercase text-gray-500">{t("journal.whatYouSaid")}</h4><Bidi code={openEntry.native_language} as="p" className="mt-1">{openEntry.native_transcript}</Bidi></div>
            <div className="nb-card p-5 bg-sky">
              <h4 className="text-xs font-bold uppercase text-gray-600">{t("journal.howToSay", { lang: langName(openEntry.target_language) })}</h4>
              <Bidi code={openEntry.target_language} as="p" className="mt-1 text-lg font-medium">{openEntry.target_text}</Bidi>
              {ttsOK(targetCode) && <button onClick={() => listen(openEntry.target_text)} disabled={playing} className="nb-btn-mint py-2 px-4 text-sm mt-3" data-testid="journal-detail-listen"><Volume2 className="w-4 h-4 inline" /> {t("common.listen")}</button>}
            </div>
            {openEntry.vocabulary?.length > 0 && (
              <div className="nb-card p-4">
                <h4 className="font-cabinet font-bold text-sm uppercase text-gray-600 mb-2">{t("journal.usefulWords")}</h4>
                {openEntry.vocabulary.map((v, i) => (
                  <div key={i} className="flex justify-between border-b border-gray-100 py-1"><Bidi code={openEntry.target_language} className="font-bold">{v.word}</Bidi><Bidi code={openEntry.native_language} className="text-sm text-gray-500">{v.translation}</Bidi></div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => renameEntry(openEntry)} className="nb-btn-outline py-2 px-4 text-sm"><Pencil className="w-4 h-4 inline" /> {t("journal.rename")}</button>
              <button onClick={() => deleteEntry(openEntry.entry_id)} className="nb-btn-outline py-2 px-4 text-sm"><Trash2 className="w-4 h-4 inline" /> {t("common.delete")}</button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
